import { Flex } from '@chakra-ui/react';
import { AiScoringLoader } from '../components';
import { AiIdealCandidateChatBox } from './AiIdealCandidateChatBox';
import { ProcessedAttribute } from '../utils/attributeValidation';
import { debounce } from 'lodash-es';
import React, { memo, useEffect, useRef } from 'react';
import { AiScoringConstants } from '../aiScoringConstants';

const DEBOUNCE_TIME = 500;

const { loaderSteps } = AiScoringConstants;

export const AiICP = memo(
  ({
    jobId,
    isIcpLoading,
    icpData,
    setIcpData,
    aiScoringData,
    getIcpCategoriesMutation,
    isIcpGenerating,
    onVerifyIcp,
  }: {
    jobId: string;
    isIcpLoading: boolean;
    icpData: {
      data: string;
      categories: string[];
      isModified: boolean;
    };
    setIcpData: React.Dispatch<
      React.SetStateAction<{
        data: string;
        categories: string[];
        isModified: boolean;
      }>
    >;
    attributes: ProcessedAttribute[];
    // saveIcpMutation: any; // Commented out - can be uncommented in future
    isIcpGenerating: boolean;
    getIcpCategoriesMutation: any;
    aiScoringData: any;
    setActiveStep: (step: number) => void;
    onVerifyIcp?: () => void;
  }) => {
    // Use useRef to store the debounced function so it persists across renders
    const debouncedFetchCategoriesRef = useRef(
      debounce((jobId: string, icp: string) => {
        getIcpCategoriesMutation.mutate({ jobId, icp });
      }, DEBOUNCE_TIME)
    );

    // Cleanup on unmount
    useEffect(() => {
      const debouncedFn = debouncedFetchCategoriesRef.current;
      return () => {
        debouncedFn.cancel();
      };
    }, []);

    // Handle mutation response
    useEffect(() => {
      if (getIcpCategoriesMutation.isSuccess && getIcpCategoriesMutation.data) {
        setIcpData((prev) => ({
          ...prev,
          categories: getIcpCategoriesMutation.data.categories || [],
        }));
      }
    }, [
      getIcpCategoriesMutation.isSuccess,
      getIcpCategoriesMutation.data,
      setIcpData,
    ]);

    // Show loader if any mutation is loading
    if (isIcpLoading) {
      return (
        <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
          <AiScoringLoader
            isStep
            config={isIcpGenerating ? loaderSteps.icp : loaderSteps.attributes}
          />
        </Flex>
      );
    }

    return (
      <Flex w="100%" h="100%" alignItems="center" justifyContent="center">
        <AiIdealCandidateChatBox
          value={icpData?.data}
          onSubmit={(val) => {
            // Update ICP data to match the submitted value
            setIcpData((prev) => ({
              ...prev,
              data: val,
              isModified: true, // Mark as modified so onVerifyIcp will process it
            }));

            // Use the proper verification flow instead of direct mutation
            if (onVerifyIcp) {
              onVerifyIcp();
            }
          }}
          onChange={(val) => {
            // Store the raw value without trimming to preserve user's exact input
            const originalSavedData = aiScoringData?.icp ?? '';

            setIcpData((prev) => ({
              ...prev,
              data: val,
              isModified: true,
            }));

            // Only fetch categories if content meaningfully changed (ignore whitespace-only changes)
            const trimmedVal = val.trim();
            const trimmedOriginal = originalSavedData.trim();
            if (trimmedVal !== trimmedOriginal && trimmedVal.length > 0) {
              debouncedFetchCategoriesRef.current(jobId, trimmedVal);
            }
          }}
          onNoChange={(_val) => {
            // Use the unified verification flow for consistency
            // This ensures all three entry points (Enter, Tab, Next button) behave the same way
            if (onVerifyIcp) {
              onVerifyIcp();
            }
          }}
          highlightedItems={
            Array.isArray(icpData?.categories) ? icpData?.categories : []
          }
          isCategoriesLoading={getIcpCategoriesMutation.isPending}
        />
      </Flex>
    );
  }
);

AiICP.displayName = 'AiICP';
